// CB:BEGIN contractor-documents-modal
import React, { useState, useEffect } from 'react';
import { useApi } from '../../lib/useApi';
import { FaFile, FaDownload, FaTimes } from 'react-icons/fa';

interface ContractorDoc {
  id: string;
  type: string;
  fileName: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  createdAt: string;
}

interface ContractorDocumentsModalProps {
  contractorId: string;
  contractorName?: string;
  isOpen: boolean;
  onClose: () => void;
}

const TYPE_LABELS: Record<string, string> = {
  LICENSE: 'Contractor License',
  INSURANCE_COI: 'Insurance Certificate (COI)',
  WORKERS_COMP: "Workers' Comp",
  WORKERS_COMP_EXEMPTION: "Workers' Comp Exemption",
  W9: 'W-9',
  AGREEMENT_SIGNED_PDF: 'Platform Agreement',
};

const ContractorDocumentsModal: React.FC<ContractorDocumentsModalProps> = ({
  contractorId,
  contractorName,
  isOpen,
  onClose
}) => {
  const api = useApi();
  const [documents, setDocuments] = useState<ContractorDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && contractorId) {
      fetchDocuments();
    }
  }, [isOpen, contractorId]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.get<{ documents: ContractorDoc[] }>(
        `/api/admin/verification-queue/${contractorId}`
      );
      setDocuments((data as any).documents || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch documents');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc: ContractorDoc) => {
    try {
      setDownloadingId(doc.id);
      const response = await api.get<{ url: string }>(
        `/api/admin/verification-queue/${contractorId}/documents/${doc.id}/download`
      );
      const a = document.createElement('a');
      a.href = response.url;
      a.target = '_blank';
      a.download = doc.fileName || `${doc.type}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading document:', err);
      setError(err instanceof Error ? err.message : 'Failed to download document');
    } finally {
      setDownloadingId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
    }}>
      <div style={{
        background: 'white', borderRadius: 12, width: '100%', maxWidth: 600,
        maxHeight: '80vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            Documents — {contractorName || 'Contractor'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#6b7280' }}>
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {error && (
            <div style={{ padding: 12, background: '#fee2e2', borderRadius: 8, color: '#dc2626', marginBottom: 16, fontSize: 14 }}>
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading documents…</div>
          ) : documents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
              <FaFile size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
              <p>No documents uploaded yet.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {documents.map((doc) => (
                <div key={doc.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 14px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb'
                }}>
                  <FaFile size={18} style={{ color: '#6b7280', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {TYPE_LABELS[doc.type] || doc.type}
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>
                      {doc.fileName || 'No filename'}{doc.sizeBytes ? ` · ${Math.round(doc.sizeBytes / 1024)} KB` : ''}
                      {' · '}Uploaded {new Date(doc.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownload(doc)}
                    disabled={downloadingId === doc.id}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '6px 12px', background: '#2563eb', color: 'white',
                      border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                      opacity: downloadingId === doc.id ? 0.6 : 1
                    }}
                  >
                    <FaDownload size={12} />
                    {downloadingId === doc.id ? 'Opening…' : 'View'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', textAlign: 'right' }}>
          <button onClick={onClose} className="btn btn-secondary" style={{ fontSize: 14 }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractorDocumentsModal;
// CB:END contractor-documents-modal
